import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearAuthData, selectUserInformations, updateTwoFactorAuth, updateUserInformation } from '../../features/authSlice';
import ToggleSwitch from '../../components/reusable/ToggleSwitch';
import EditIcon from '../../assets/svg/EditIcon.svg';
import LogoutWhite from '../../assets/svg/LogoutWhite.svg';
import axios from 'axios';
import { API } from '../../shared/api/Api';
import Loader from '../../components/Live/Loader';
import { showToast } from '../../utils/utils';
import { Logout } from '../../services/axiosServices';
import Modal from '../../components/Modal';
import ChangePassword from '../../components/ChangePassword';
import EditProfile from './EditProfile';
import { updateUserInfo, userDetails } from '../../services/ProfileService';
import { persistor } from '../../redux/store';
import { clearAuthCookies } from '../../utils/cookieUtils';

const Profile = () => {
  const user = useSelector(selectUserInformations);
  const dispatch = useDispatch();

  const [isChecked2FA, setIsChecked2FA] = useState(user.twoFactorAuth);
  const [isCheckedNotification, setIsCheckedNotification] = useState(false);
  const [loader, setLoader] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalEditOpen, setIsModalEditOpen] = useState(false);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleOpenEditModal = () => setIsModalEditOpen(true);
  const handleEditCloseModal = () => setIsModalEditOpen(false);

  const handleSubmit = async (password, newPassword, confirmNewPassword) => {
    try {
      const { data } = await axios.post(API.Users.app_users_actions_updatePassword, {
        "old_password": password,
        "new_password": newPassword,
        "new_password_confirmation": confirmNewPassword
      }, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          lang: user.lang,
          Accept: 'application/json'
        }
      });

      if (data.status === 200 && data.errorCode === 0) {
        showToast('Password Updated', data.message, 'success');
        dispatch(clearAuthData());
        handleCloseModal();
      } else {
        showToast('Oops, Something went wrong', data.response.data.message, 'error');
      }
    } catch (error) {
      if (error.response?.data?.message === 'Oops! There seems to be a validation error!') {
        if (error.response.data.data.new_password) {
          showToast('Oops, Something went wrong', error.response.data.data.new_password, 'error');
        }
        if (error.response.data.data.new_password_confirmation) {
          showToast('Oops, Something went wrong', error.response.data.data.new_password_confirmation, 'error');
        }
      } else {
        showToast('Oops, Something went wrong', error.response?.data?.message || 'An unknown error occurred', 'error');
      }
    }
  };

  const handleUpdateInfo = async (data, setFormErrors) => {
    try {
      const response = await updateUserInfo(data, user.token);
      if (response.data.status === 200 && response.data.errorCode === 0) {
        showToast('Profile Information', response.data.message || 'Profile updated successfully.', 'success');
        dispatch(updateUserInformation(response.data.data));
        setIsModalEditOpen(false);
      } else {
        showToast('Error', response.data.message, 'error');
      }
    } catch (error) {
      const status = error?.response?.status || error?.status;
      const payload = error?.response?.data || error?.data || {};

      if (status === 400 && payload?.data) {
        const validationErrors = Object.keys(payload.data).reduce((acc, key) => {
          acc[key] = Array.isArray(payload.data[key]) ? payload.data[key][0] : payload.data[key];
          return acc;
        }, {});

        if (typeof setFormErrors === 'function') {
          setFormErrors(validationErrors);
        }

        const message = payload?.message || 'Validation error occurred.';
        showToast('Validation error', message, 'error');
      } else {
        const message = payload?.message || 'There is an error, please try again later';
        showToast('Oops, there is an error', message, 'error');
      }
    }
  };

  const handleToggleChange2FA = async () => {
    try {
      setLoader(true);
      const { data } = await axios.get(API.Users.app_users_actions_update2FA, {
        headers: { Authorization: `Bearer ${user.token}` }
      });

      if (data.status === 200 && data.errorCode === 0) {
        setIsChecked2FA(!isChecked2FA);
        dispatch(updateTwoFactorAuth(data.data.twoFactorAuth));
        showToast('Update 2FA', data.message, 'success');
      } else {
        showToast('Something went wrong', data.message, 'error');
      }
    } catch (error) {
      showToast('Something went wrong', error.response?.data?.message || 'There is an error, try again.', 'error');
    } finally {
      setLoader(false);
    }
  };

  const handleToggleChangeNotification = () => {
    setIsCheckedNotification(!isCheckedNotification);
  };

  const handleLogout = async () => {
    setLoader(true);
    try {
      const response = await Logout(user);

      if (response.status === 200 && response.errorCode === 0) {
        dispatch(clearAuthData());

        try {
          clearAuthCookies();
        } catch (cookieError) {
          console.error('Failed to clear cookies:', cookieError);
        }

        persistor.purge(); // Clears persisted data
        showToast("Bye Bye", response.message || "Logged out successfully.", "success");
      } else {
        showToast("Oops, Something went wrong", response.message || "Logout failed.", "error");
      }
    } catch (error) {
      showToast("Oops, Something went wrong", "Please try again.", "error");
    } finally {
      setLoader(false);
    }
  };

  return (
    <div className="flex flex-col w-full min-h-screen justify-center items-center bg-[#EBEBEB] px-4 py-10 md:py-0 md:px-0">
      <div className="flex flex-col w-full max-w-[1645px] h-auto relative space-y-6">
        {/* Floating Profile Image */}
        <div className="absolute left-1/2 transform -translate-x-1/2 md:-top-[10%] md:left-[3%] md:translate-x-0 
          rounded-full border-12 md:border-[12px] border-[#EBEBEB] w-[150px] h-[150px] md:w-[250px] md:h-[250px] 
          lg:w-[300px] lg:h-[300px] z-10 overflow-hidden shadow-2xl">
          <img
            className="w-full h-full object-cover"
            src={user.image_url}
            alt="Profile"
          />
        </div>

        {/* Name Section */}
        <div className="relative text-center top-[8rem] md:top-0 md:text-left px-4 md:px-0 md:ml-[23%] mt-4 md:mt-8">
          <h1 className="text-3xl md:text-5xl lg:text-[64px] font-medium text-[#134F98] break-words">
            {user.name}
          </h1>
        </div>
        {/* Content Container */}
        <div className="flex flex-col w-full h-full border-1 rounded-2xl bg-white pt-32 md:pt-0">
          {/* Email & Logout Section */}
          <div className="flex flex-col md:flex-row items-center justify-between h-auto md:h-[93px] p-4 md:p-8 
            bg-[#134F98] rounded-tr-2xl rounded-tl-2xl space-y-4 md:space-y-0">
            <span className="text-lg md:translate-x-[20rem] md:text-xl lg:text-[24px] font-medium text-white text-center md:text-left">
              {user.email}
            </span>
            <div
              className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={handleLogout}
            >
              <img className="w-8 h-8" src={LogoutWhite} alt="Logout" />
              <span className="text-lg md:text-xl lg:text-[24px] font-medium text-white">
                Logout
              </span>
            </div>
          </div>

          {/* Settings Sections */}
          <div className="space-y-4 md:space-y-0">
            {/* 2FA Section */}
            <div className="flex flex-col md:flex-row items-center justify-between h-auto md:h-[110px] p-4 md:p-8 
              bg-white border-b-2 border-b-[#DADADA]">
              <span className="text-base md:text-lg lg:text-[24px] text-[#858585] mb-2 md:mb-0">
                Enable 2-Steps Verification
              </span>
              {loader ? (
                <Loader />
              ) : (
                <ToggleSwitch
                  checked={isChecked2FA}
                  onChange={handleToggleChange2FA}
                />
              )}
            </div>

            {/* Notifications Section */}
            <div className="flex flex-col md:flex-row items-center justify-between h-auto md:h-[110px] p-4 md:p-8 
              bg-white border-b-2 border-b-[#DADADA]">
              <span className="text-base md:text-lg lg:text-[24px] text-[#858585] mb-2 md:mb-0">
                Enable Notifications
              </span>
              <ToggleSwitch
                checked={isCheckedNotification}
                onChange={handleToggleChangeNotification}
              />
            </div>

            {/* Change Password Section */}
            <div className="flex flex-col md:flex-row items-center justify-between h-auto md:h-[110px] p-4 md:p-8 
              bg-white border-b-2 border-b-[#DADADA]">
              <span className="text-base md:text-lg lg:text-[24px] text-[#858585] mb-2 md:mb-0">
                Change Password
              </span>
              <button
                className="hover:scale-110 transition-transform"
                onClick={handleOpenModal}
              >
                <img src={EditIcon} alt="Edit" className="w-8 h-8 md:w-[37px] md:h-[37px]" />
              </button>
            </div>

            {/* Edit Profile Section */}
            <div className="flex flex-col md:flex-row items-center justify-between h-auto md:h-[110px] p-4 md:p-8 
              bg-white border-b-2 border-b-[#DADADA]">
              <span className="text-base md:text-lg lg:text-[24px] text-[#858585] mb-2 md:mb-0">
                Edit Profile Informations
              </span>
              <button
                className="hover:scale-110 transition-transform"
                onClick={handleOpenEditModal}
              >
                <img src={EditIcon} alt="Edit" className="w-8 h-8 md:w-[37px] md:h-[37px]" />
              </button>
            </div>

            {/* Empty Bottom Section */}
            <div className="h-[80px] bg-white rounded-br-2xl rounded-bl-2xl" />
          </div>
        </div>
      </div>

      {/* Modals - Keep existing implementation */}
      <Modal
        title="Change Password"
        isOpen={isModalOpen}
        className="w-[90%] md:w-[693px] h-auto md:h-[753px]"
        withHeader={false}
        withButtons={false}
      >
        <ChangePassword
          onSubmit={handleSubmit}
          submitText="Submit"
          onClose={handleCloseModal}
        />
      </Modal>

      <Modal
        title="Edit Profile Informations"
        isOpen={isModalEditOpen}
        className="w-[90%] md:w-[693px] h-auto md:h-[753px]"
        withHeader={false}
        withButtons={false}
      >
        <EditProfile handleUpdateInfo={handleUpdateInfo} onClose={handleEditCloseModal} userDetails={user} />
      </Modal>
    </div>
  );
};

export default Profile;